import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldAlert,
  Phone,
  MapPin,
  Users,
  CheckCircle2,
  Clock,
  Train,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Loader2,
  Navigation,
  Trash2,
  Search,
  Filter,
  AlertCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Link } from 'react-router-dom';

import { BACKEND_URL } from '../../config';
const API_BASE = BACKEND_URL;

const AdminSosAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'RESOLVED'
  const [searchQuery, setSearchQuery] = useState('');

  const fetchAlerts = useCallback(async () => {
    const token = localStorage.getItem('tp_admin_token');
    try {
      const res = await axios.get(`${API_BASE}/api/sos/admin/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(res.data.alerts || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast({
          title: 'Admin Session Required',
          description: 'Please login at /admin/login to view and manage SOS alerts.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Failed to load SOS alerts', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'SOS Alerts & Family Records – Admin Panel';
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 10000); // 10s live polling
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleUpdateStatus = async (alertId, newStatus) => {
    setUpdatingId(alertId);
    const token = localStorage.getItem('tp_admin_token');
    try {
      await axios.put(
        `${API_BASE}/api/sos/admin/alerts/${alertId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: `Alert marked as ${newStatus}` });
      fetchAlerts();
    } catch (err) {
      toast({ title: 'Error updating alert status', variant: 'destructive' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm(`Are you sure you want to delete SOS record ${alertId}?`)) return;
    setDeletingId(alertId);
    const token = localStorage.getItem('tp_admin_token');
    try {
      await axios.delete(`${API_BASE}/api/sos/admin/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Alert record deleted successfully' });
      fetchAlerts();
    } catch (err) {
      toast({ title: 'Failed to delete alert', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleClearAllTestAlerts = async () => {
    if (!window.confirm('Clear all historical and duplicate test SOS alerts? This cannot be undone.')) return;
    const token = localStorage.getItem('tp_admin_token');
    try {
      const res = await axios.post(`${API_BASE}/api/sos/admin/alerts/cleanup-test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: res.data.message || 'All test alerts cleared' });
      fetchAlerts();
    } catch (err) {
      toast({ title: 'Failed to clear alerts', variant: 'destructive' });
    }
  };

  const activeCount = alerts.filter((a) => a.status === 'ACTIVE').length;
  const resolvedCount = alerts.filter((a) => a.status === 'RESOLVED').length;

  const filteredAlerts = alerts.filter((a) => {
    const matchesFilter =
      filterStatus === 'ALL' ||
      (filterStatus === 'ACTIVE' && a.status === 'ACTIVE') ||
      (filterStatus === 'RESOLVED' && a.status === 'RESOLVED');

    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      (a.passenger_name && a.passenger_name.toLowerCase().includes(q)) ||
      (a.user_mobile && a.user_mobile.includes(q)) ||
      (a.id && a.id.toLowerCase().includes(q)) ||
      (a.train_number && a.train_number.toLowerCase().includes(q));

    return matchesFilter && matchesSearch;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      
      {/* Header with Stats */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white rounded-3xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-500/30">
            <ShieldAlert className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <h1 className="text-xl font-black text-gray-900">SOS Emergency Alerts & Family Directory</h1>
            <p className="text-xs text-gray-500">Live monitoring of distress signals and linked emergency contacts</p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-700 font-extrabold text-xs">
            <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-ping"></span>
            <span>{activeCount} Active Emergency</span>
          </div>

          <button
            onClick={fetchAlerts}
            className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
            title="Refresh feed"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          {alerts.length > 0 && (
            <button
              onClick={handleClearAllTestAlerts}
              className="px-3 py-2 bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600 rounded-xl text-xs font-bold transition-colors flex items-center gap-1 border border-gray-200"
              title="Clear old test records"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear Test Alerts
            </button>
          )}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilterStatus('ALL')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
              filterStatus === 'ALL'
                ? 'bg-gray-900 text-white shadow'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Alerts ({alerts.length})
          </button>
          <button
            onClick={() => setFilterStatus('ACTIVE')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
              filterStatus === 'ACTIVE'
                ? 'bg-red-600 text-white shadow'
                : 'bg-red-50 text-red-700 hover:bg-red-100'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-current"></span>
            Active Only ({activeCount})
          </button>
          <button
            onClick={() => setFilterStatus('RESOLVED')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
              filterStatus === 'RESOLVED'
                ? 'bg-emerald-600 text-white shadow'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            Resolved ({resolvedCount})
          </button>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search passenger, mobile, ID..."
            className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>
      </div>

      {/* Alerts Feed */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
          <p className="text-xs text-gray-500 font-medium">Fetching real-time SOS feeds...</p>
        </div>
      ) : filteredAlerts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-gray-200 p-8 space-y-3">
          <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h3 className="text-base font-bold text-gray-900">No SOS Alerts Found</h3>
          <p className="text-xs text-gray-500 max-w-sm mx-auto">
            {searchQuery || filterStatus !== 'ALL'
              ? 'No records match your search or filter criteria.'
              : 'Emergency distress calls from trains and stations will automatically populate here in real time.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAlerts.map((a) => {
            const isActive = a.status === 'ACTIVE';
            const isResolved = a.status === 'RESOLVED';
            const hasGps = a.latitude && a.longitude;
            const mapsUrl = hasGps
              ? `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`
              : null;

            return (
              <div
                key={a.id}
                className={`bg-white rounded-3xl p-6 border shadow-sm transition-all space-y-5 ${
                  isActive
                    ? 'border-red-500 ring-2 ring-red-500/20 bg-red-50/10'
                    : isResolved
                    ? 'border-gray-200 opacity-80'
                    : 'border-amber-300'
                }`}
              >
                {/* Alert Top Bar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider flex items-center gap-1.5 ${
                        isActive
                          ? 'bg-red-600 text-white animate-pulse'
                          : isResolved
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {isActive ? '🚨 ACTIVE SOS' : isResolved ? '✓ RESOLVED' : '🟡 IN PROGRESS'}
                    </span>
                    <span className="text-xs font-mono font-bold text-gray-700">{a.id}</span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(a.created_at).toLocaleString()}
                    </span>
                  </div>

                  {/* Status & Delete Action Buttons */}
                  <div className="flex items-center gap-2">
                    {isActive && (
                      <button
                        onClick={() => handleUpdateStatus(a.id, 'DISPATCHED')}
                        disabled={updatingId === a.id}
                        className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-sm transition-colors"
                      >
                        Mark Dispatched
                      </button>
                    )}
                    {!isResolved && (
                      <button
                        onClick={() => handleUpdateStatus(a.id, 'RESOLVED')}
                        disabled={updatingId === a.id}
                        className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm transition-colors flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Mark Resolved
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteAlert(a.id)}
                      disabled={deletingId === a.id}
                      className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                      title="Delete Record"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Incident Main Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  
                  {/* Passenger Card */}
                  <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-2">
                    <span className="text-[10px] uppercase font-bold text-gray-500">Passenger Details</span>
                    <h3 className="font-bold text-gray-900 text-base">{a.passenger_name}</h3>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <Phone className="w-3.5 h-3.5 text-red-600" />
                      <span>{a.user_mobile || 'Phone not provided'}</span>
                      {a.user_mobile && (
                        <a
                          href={`tel:${a.user_mobile}`}
                          className="ml-auto text-[11px] font-bold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-2 py-0.5 rounded"
                        >
                          Call
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Train & Coach Details */}
                  <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-2">
                    <span className="text-[10px] uppercase font-bold text-gray-500">Train Location</span>
                    <p className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                      <Train className="w-4 h-4 text-orange-600" />
                      Train: {a.train_number || 'N/A'} {a.coach ? `· Coach: ${a.coach}` : ''} {a.berth ? `· Berth: ${a.berth}` : ''}
                    </p>
                    <p className="text-xs text-gray-600">Station Halt: {a.station || 'In Transit'}</p>
                  </div>

                  {/* GPS Coordinates & Live Map Link */}
                  <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-2">
                    <span className="text-[10px] uppercase font-bold text-gray-500">Live GPS Coordinates</span>
                    {hasGps ? (
                      <div>
                        <p className="font-mono text-xs font-bold text-gray-900">
                          {a.latitude.toFixed(5)}, {a.longitude.toFixed(5)}
                        </p>
                        <div className="flex gap-2 mt-1.5">
                          <Link
                            to={`/sos/track/${a.id}`}
                            target="_blank"
                            className="text-[11px] font-bold text-red-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" /> Live Tracking UI
                          </Link>
                          {mapsUrl && (
                            <a
                              href={mapsUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] font-bold text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Navigation className="w-3 h-3" /> Google Maps
                            </a>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">GPS not provided</p>
                    )}
                  </div>

                </div>

                {/* Emergency Situation Message */}
                {a.message && (
                  <div className="p-3.5 bg-amber-50 rounded-2xl border border-amber-200 text-xs text-amber-900">
                    <span className="font-bold">Distress Message: </span>
                    <span>"{a.message}"</span>
                  </div>
                )}

                {/* NOTIFIED FAMILY MEMBERS TABLE */}
                <div className="border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
                    <div className="flex items-center gap-2 text-xs font-black text-gray-800">
                      <Users className="w-4 h-4 text-orange-600" />
                      <span>Linked Family Members Notified ({a.family_members?.length || 0} Contacts)</span>
                    </div>
                  </div>

                  {a.family_members && a.family_members.length > 0 ? (
                    <div className="divide-y divide-gray-100 bg-white">
                      {a.family_members.map((m, idx) => (
                        <div key={idx} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 font-bold flex items-center justify-center text-[11px]">
                              {idx + 1}
                            </span>
                            <div>
                              <span className="font-bold text-gray-900">{m.name}</span>
                              <span className="ml-2 text-gray-500 font-medium">({m.relationship})</span>
                              {m.is_primary && (
                                <span className="ml-2 text-[10px] uppercase font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                                  Primary
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="font-mono text-gray-700">+91 {m.contact_number}</span>
                            <a
                              href={`tel:${m.contact_number}`}
                              className="px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg font-bold text-[11px] flex items-center gap-1 transition-colors"
                            >
                              <Phone className="w-3 h-3" /> Call Contact
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-xs text-gray-400 bg-white">
                      No family members were configured in this user's SSO account.
                    </div>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default AdminSosAlerts;
